import dayjs from "dayjs";

/**
 * Selection and status logic for the Desktop App page, kept free of React so
 * it can be tested on its own.
 *
 * Licence dates are plain "yyyy-mm-dd" strings, the same as Supabase stores
 * them. A licence counts as current while its expiry date is after today (UTC),
 * which is the rule Supabase uses to decide whether someone already has
 * access, so the page and the server never disagree about who gets skipped.
 */

// Mirrors LicenceStatus.graceDays in the desktop app: installs keep running
// this many days past the expiry date.
export const GRACE_DAYS = 7;

export function addDaysIso(isoDate, days) {
    const [year, month, day] = isoDate.slice(0, 10).split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day + days))
        .toISOString()
        .slice(0, 10);
}

/**
 * Where a licence stands today, as the desktop app sees it: "active" until its
 * expiry date, then "grace" for GRACE_DAYS (the app still runs, warning on each
 * launch), then "expired".
 */
export function licenceStatus(licence, today = todayIsoDate()) {
    if (!licence || !licence.expiryDate) return { status: "expired" };
    if (licence.expiryDate > today) return { status: "active" };

    const stopsOn = addDaysIso(licence.expiryDate, GRACE_DAYS);
    if (today < stopsOn) {
        const daysLeft = Math.round(
            (Date.parse(stopsOn) - Date.parse(today)) / 86400000,
        );
        return { status: "grace", daysLeft: daysLeft, stopsOn: stopsOn };
    }

    return { status: "expired" };
}

/** Whether someone's latest key already runs to the website's current date. */
export function hasKeyForCurrentPeriod(licence, webExpiryDate) {
    return Boolean(
        licence && webExpiryDate && licence.expiryDate >= webExpiryDate,
    );
}

/**
 * Everyone who has had a web-issued key but not one for the current period:
 * the people "send new keys to everyone" renews.
 */
export function renewalCandidates(licenceByUid, webExpiryDate, knownUserKeys) {
    if (!webExpiryDate) return [];
    const known = new Set(knownUserKeys || []);

    return Array.from(licenceByUid.values())
        .filter(
            (licence) =>
                known.has(licence.uid) &&
                !hasKeyForCurrentPeriod(licence, webExpiryDate),
        )
        .map((licence) => licence.uid);
}

export function todayIsoDate(now = new Date()) {
    return now.toISOString().slice(0, 10);
}

export function licenceIsCurrent(licence, today = todayIsoDate()) {
    return Boolean(licence && licence.expiryDate && licence.expiryDate > today);
}

/** "1 Mac" / "3 Macs" */
export function macsLabel(count) {
    const n = Math.max(1, Number(count) || 1);
    return n === 1 ? "1 Mac" : n + " Macs";
}

/**
 * How much of a key is in use: "Activated" / "Not activated yet" for a
 * one-Mac key, "1 of 3 Macs" once a multi-Mac key has been used.
 */
export function activationLabel(licence) {
    const used = licence.activationCount ?? (licence.redeemed ? 1 : 0);
    const limit = licence.maxDevices || 1;

    if (used === 0) return "Not activated yet";
    return limit > 1 ? used + " of " + limit + " Macs" : "Activated";
}

export function formatLicenceDate(isoDate) {
    if (!isoDate) return "";
    const date = dayjs(isoDate.slice(0, 10));
    return date.isValid() ? date.format("D MMMM YYYY") : isoDate;
}

/** Each user's latest licence (by expiry), keyed by uid. */
export function latestLicenceByUid(licences) {
    const byUid = new Map();

    (licences || []).forEach((licence) => {
        if (!licence.uid) return;

        const existing = byUid.get(licence.uid);
        if (!existing || licence.expiryDate > existing.expiryDate) {
            byUid.set(licence.uid, licence);
        }
    });

    return byUid;
}

/**
 * Turns the ticked people and groups into the uids to send.
 *
 * Group members are expanded, duplicates removed, and anyone without a user
 * record dropped. People who already hold a current licence are counted
 * separately rather than sent: the server would skip them anyway.
 */
export function resolveRecipients({
    userKeys,
    groupKeys,
    groups,
    knownUserKeys,
    licenceByUid,
    webExpiryDate,
    today = todayIsoDate(),
}) {
    const known = new Set(knownUserKeys || []);
    const wanted = new Set(userKeys || []);
    const chosenGroups = new Set(groupKeys || []);

    (groups || []).forEach((group) => {
        if (!chosenGroups.has(group.key)) return;
        (group.users || []).forEach((member) => wanted.add(member.key));
    });

    const recipients = [];
    let alreadyHaveAccess = 0;

    wanted.forEach((uid) => {
        if (!known.has(uid)) return;

        const licence = licenceByUid && licenceByUid.get(uid);
        // With a date set, the server skips only people already on it;
        // anyone on an older period gets a renewal
        const skip = webExpiryDate
            ? hasKeyForCurrentPeriod(licence, webExpiryDate)
            : licenceIsCurrent(licence, today);

        if (skip) {
            alreadyHaveAccess++;
        } else {
            recipients.push(uid);
        }
    });

    return { recipients: recipients, alreadyHaveAccess: alreadyHaveAccess };
}

function plural(count, one, many) {
    return count + " " + (count === 1 ? one : many);
}

/** Snackbar text for a grantDesktopAccess result. */
export function describeGrantResult(result) {
    const parts = [];

    const renewed = result.renewed || 0;
    const firstTime = result.issued - renewed;

    if (firstTime > 0 && renewed > 0) {
        parts.push(
            "Access granted to " +
                plural(firstTime, "person", "people") +
                " and new keys sent to " +
                plural(renewed, "person", "people"),
        );
    } else if (renewed > 0) {
        parts.push("New keys sent to " + plural(renewed, "person", "people"));
    } else if (firstTime > 0) {
        parts.push("Access granted to " + plural(firstTime, "person", "people"));
    } else {
        parts.push("No new access granted");
    }

    if (result.alreadyHadAccess > 0) {
        parts.push(
            plural(result.alreadyHadAccess, "person", "people") +
                " already had a current key",
        );
    }

    const problems = [];

    if (result.noEmail > 0) {
        problems.push(
            plural(result.noEmail, "person has", "people have") +
                " no email address, so they'll only see the key in Nodiant Online",
        );
    }

    if (result.emailFailed > 0) {
        problems.push(
            plural(result.emailFailed, "email", "emails") +
                " couldn't be sent; those people can still see their key in Nodiant Online",
        );
    }

    return {
        message: parts.concat(problems).join(". ") + ".",
        variant: problems.length > 0 ? "warning" : "success",
    };
}

export const ROOT_HUB = "Root";

function byName(a, b) {
    return (a.name || "").localeCompare(b.name || "");
}

/**
 * Arranges hubs and groups the way the file navigator arranges folders: each
 * hub lists its sub-hubs, then the groups that belong to it.
 *
 * Only hubs are navigable. A hub whose parent is a plain folder hangs off the
 * nearest hub above it, and anything with no hub above it (or a group whose hub
 * has gone) sits at the top level, so nothing becomes unreachable.
 */
export function buildHubTree(folders, groups) {
    const folderByKey = new Map();
    (folders || []).forEach((folder) => folderByKey.set(folder.key, folder));

    const hubs = (folders || []).filter((folder) => folder.hub);

    function nearestHubAbove(folder) {
        const seen = new Set([folder.key]);
        let parentKey = folder.parent;

        while (parentKey && parentKey !== ROOT_HUB && !seen.has(parentKey)) {
            seen.add(parentKey);
            const parent = folderByKey.get(parentKey);
            if (!parent) break;
            if (parent.hub) return parent.key;
            parentKey = parent.parent;
        }

        return ROOT_HUB;
    }

    const hubByKey = new Map();
    const parentOf = new Map();
    const childHubs = new Map([[ROOT_HUB, []]]);
    const groupsIn = new Map([[ROOT_HUB, []]]);

    hubs.forEach((hub) => {
        hubByKey.set(hub.key, hub);
        childHubs.set(hub.key, []);
        groupsIn.set(hub.key, []);
    });

    hubs.forEach((hub) => {
        const parent = nearestHubAbove(hub);
        parentOf.set(hub.key, parent);
        childHubs.get(parent).push(hub);
    });

    (groups || []).forEach((group) => {
        const home = hubByKey.has(group.hub) ? group.hub : ROOT_HUB;
        groupsIn.get(home).push(group);
    });

    childHubs.forEach((list) => list.sort(byName));
    groupsIn.forEach((list) => list.sort(byName));

    // Every group key at or below each hub, for the "N selected" hints
    const groupKeysBelow = new Map();
    function collect(hubKey) {
        if (groupKeysBelow.has(hubKey)) return groupKeysBelow.get(hubKey);
        const keys = groupsIn.get(hubKey).map((group) => group.key);
        groupKeysBelow.set(hubKey, keys);
        childHubs.get(hubKey).forEach((child) => {
            collect(child.key).forEach((key) => keys.push(key));
        });
        return keys;
    }
    collect(ROOT_HUB);

    function pathNames(hubKey) {
        const names = [];
        let key = hubKey;
        while (key && key !== ROOT_HUB && hubByKey.has(key)) {
            names.unshift(hubByKey.get(key).name);
            key = parentOf.get(key);
        }
        return names;
    }

    return {
        hubByKey: hubByKey,
        childHubs: (hubKey) => childHubs.get(hubKey) || [],
        groupsIn: (hubKey) => groupsIn.get(hubKey) || [],
        groupKeysBelow: (hubKey) => groupKeysBelow.get(hubKey) || [],
        pathNames: pathNames,
    };
}
